// Minimum spacings and edge/end distances: ETA-11/0030 tables, EC5 Table 8.2, steel plate holes, panels, exceptions.

import { cos, currentFa, sin } from "./angles";
import { isPanelMat, isSteelMat, type EngineCtx } from "./ctx";
import { panelKindOf } from "./materials";
import { TIMBER_TIPO_MAP_REVERSE } from "./data";

export type Spacing = {
  a1: number;
  a2: number;
  a3t: number;
  a3c: number;
  a4t: number;
  a4c: number;
  outOfRangeDensity?: boolean;
  colonna?: string;
};

// Rounding to the NEAREST whole mm (user request 2026-10-14, alignment with the reference reports: 29.02 → 29,
// 29.6 → 30): the value may drop at most 0.5 mm below the code minimum (accepted tolerance). The name ceilMm is
// historical. The 1e-9 tolerance avoids numeric noise on integer values (5·9 → 45.000000000000004).
export function ceilMm(x: number): number {
  return Number.isFinite(x) ? Math.round(x + 1e-9) : x;
}
export function ceilSpacing<T extends Partial<Spacing>>(sp: T): T {
  const o: T = { ...sp };
  (["a1", "a2", "a3t", "a3c", "a4t", "a4c"] as const).forEach((k) => {
    if (k in o) (o as Record<string, unknown>)[k] = ceilMm(o[k] as number);
  });
  return o;
}

export function etaSpacingPredrilled(d: number, alphaDeg: number): Spacing {
  const a = Math.abs(alphaDeg);
  return {
    a1: (4 + Math.abs(cos(a))) * d,
    a2: (3 + Math.abs(sin(a))) * d,
    a3t: Math.max(7 * d, (7 + 5 * cos(a)) * d),
    a3c: 7 * d,
    a4t: Math.max(3 * d, (3 + 4 * sin(a)) * d),
    a4c: 3 * d,
  };
}
export function etaSpacingHBS(d: number, alphaDeg: number): Spacing {
  const a = Math.abs(alphaDeg);
  return {
    a1: (5 + 5 * Math.abs(cos(a))) * d,
    // a2 = 5·d constant (ETA-11/0030 HBS table "non-predrilled", confirmed by the user on the original text)
    a2: 5 * d,
    // floored at 10d (= a3c): outside -90..90 (e.g. alpha = 180° for element 1) cos is negative
    a3t: Math.max(10 * d, (10 + 5 * cos(a)) * d),
    a3c: 10 * d,
    a4t: (5 + 5 * Math.abs(sin(a))) * d,
    a4c: 5 * d,
  };
}
export function etaSpacingKKT(d: number): Spacing {
  return { a1: 8 * d, a2: 4 * d, a3t: 12 * d, a3c: 5 * d, a4t: 5 * d, a4c: 4 * d };
}
// LVL softwood, wide face (HBS/HBSH/HBSP/SHS/KKF/TBS, 5≤d≤10 mm): HBS format with coefficient 7 on a1.
export function etaSpacingLvlWide(d: number, alphaDeg: number): Spacing {
  const a = Math.abs(alphaDeg);
  return {
    a1: (5 + 7 * Math.abs(cos(a))) * d,
    a2: 5 * d,
    a3t: Math.max(10 * d, (10 + 5 * cos(a)) * d),
    a3c: 10 * d,
    a4t: (5 + 5 * Math.abs(sin(a))) * d,
    a4c: 5 * d,
  };
}
// LVL softwood, narrow edge, only axially loaded VGZ/VGZH/VGS/VGSH d = 7/9 mm: a1,CG/a2,CG replace a3/a4 (no a2:
// it stays the one of the base formula).
export function etaSpacingLvlAxialEdge(d: number): Omit<Spacing, "a2"> {
  return { a1: 10 * d, a3t: 12 * d, a3c: 12 * d, a4t: 3 * d, a4c: 3 * d };
}
export function isLvlWideFamily(fam: string): boolean {
  return /^HBS|^TBS|^SHS|^KKF/i.test(fam);
}
export function isLvlAxialEdgeFamily(fam: string): boolean {
  return /^VG[ZS]/i.test(fam);
}

// CLT minimum distances (ETA-11/0030 Annex B): wide — a1=4d, a2=2.5d, a3,c=a3,t=6d, a4,c=2.5d, a4,t=6d;
// narrow — a1=10d, a2=4d, a3,c=7d, a3,t=12d, a4,c=3d, a4,t=6d.
export function etaSpacingClt(lato: string, d: number): Spacing {
  if (lato === "wide") {
    return ceilSpacing({ a1: 4 * d, a2: 2.5 * d, a3t: 6 * d, a3c: 6 * d, a4t: 6 * d, a4c: 2.5 * d });
  }
  return ceilSpacing({ a1: 10 * d, a2: 4 * d, a3t: 12 * d, a3c: 7 * d, a4t: 6 * d, a4c: 3 * d });
}

// EC5 Table 8.2 (nails / screws d<=6 mm) in full: "predrilled", "non-predrilled ρk≤420", "non-predrilled
// 420<ρk≤500" (beyond 500 the last column, outOfRangeDensity), d<5 / d>=5 thresholds on a1/a4,t; steel-to-timber
// (§8.3.1.4): only a1,a2 ×0.7. Only used as base for the non-HBS non-predrilled screws (ETA: "as for nails").
export function ec5MinSpacing(
  d: number,
  alphaDeg: number,
  opts?: { predrilled?: boolean; rhok?: number; steelToTimber?: boolean },
): Spacing {
  const o = opts || {};
  const predrilled = o.predrilled !== false;
  const steelToTimber = !!o.steelToTimber;
  const a = Math.abs(alphaDeg);
  const cA = Math.abs(cos(a)),
    sA = Math.abs(sin(a));
  const dSmall = d < 5;
  // a3,t valid for -90°<=alpha<=90°: outside, signed cos floored at a3,c of the same column
  const cSigned = cos(a);
  let a1: number, a2: number, a4t: number, a4c: number, a3t: number, a3c: number;
  let outOfRangeDensity = false;
  let colonna: string;
  if (predrilled) {
    colonna = "con preforatura";
    a1 = (4 + cA) * d;
    a2 = (3 + sA) * d;
    a4t = (dSmall ? 3 + 2 * sA : 3 + 4 * sA) * d;
    a4c = 3 * d;
    a3c = 7 * d;
    a3t = Math.max(a3c, (7 + 5 * cSigned) * d);
  } else {
    const rhok = o.rhok as number;
    outOfRangeDensity = rhok > 500;
    const denseCol = rhok > 420;
    colonna = denseCol ? "senza preforatura, 420<ρk≤500" : "senza preforatura, ρk≤420";
    if (denseCol) {
      a1 = (7 + 8 * cA) * d;
      a2 = 7 * d;
      a4t = (dSmall ? 7 + 2 * sA : 7 + 5 * sA) * d;
      a4c = 7 * d;
      a3c = 15 * d;
      a3t = Math.max(a3c, (15 + 5 * cSigned) * d);
    } else {
      a1 = (dSmall ? 5 + 5 * cA : 5 + 7 * cA) * d;
      a2 = 5 * d;
      a4t = (dSmall ? 5 + 2 * sA : 5 + 5 * sA) * d;
      a4c = 5 * d;
      a3c = 10 * d;
      a3t = Math.max(a3c, (10 + 5 * cSigned) * d);
    }
  }
  if (steelToTimber) {
    a1 *= 0.7;
    a2 *= 0.7;
  }
  return { a1, a2, a3t, a3c, a4t, a4c, outOfRangeDensity, colonna };
}

// Edge/end distances of a screw through a STEEL PLATE (hole criterion, nominal hole d0 = 1.1·d): a3,t,1 = 1.2·d0,
// a4,t,1 = a4,c,1 = 1.5·d0. Replaces a3,t/a4,t/a4,c of element 1; a1/a2 are untouched.
export function steelPlateMinDistances(d: number): { a3t: number; a4t: number; a4c: number } {
  const d0 = 1.1 * d;
  const a4 = 1.5 * d0;
  return ceilSpacing({ a3t: 1.2 * d0, a4t: a4, a4c: a4 });
}

// Element 1 = panel (aligned with the MY PROJECT reference report 2026-10-13): no grain → α = 0 and no ρk
// dependency (column ρk ≤ 420 of Table 8.2; non-predrilled a1 = 10d also for d ≥ 5 mm). Plywood (EC5 §8.3.1.3(2)):
// 3d unloaded, (3+4·sin α)·d loaded, α = 0. a1,a2 ×0.85 in applyEtaSpacingEccezioni.
export function panelSpacing1(ctx: EngineCtx, d: number, _alphaDeg: number, predrilled: boolean): Spacing {
  const kind = panelKindOf(ctx.v.mat1Specie, ctx.v.mat1Classe);
  const sp = ec5MinSpacing(d, 0, { predrilled, rhok: 400, steelToTimber: false });
  if (!predrilled) sp.a1 = 10 * d;
  if (kind === "plywood") {
    sp.a3c = 3 * d;
    sp.a4c = 3 * d;
    sp.a3t = 7 * d; // (3+4·cos 0)·d
    sp.a4t = 3 * d; // (3+4·sin 0)·d
  }
  return sp;
}

// ETA-11/0030 p.15: the table a1=(5+5|cosα|)·d, a2=5·d, … is valid ONLY for "HBS" screws (d = 6–12 mm, self-drilling
// tip, non-predrilled softwood). The other screws loaded in shear in non-predrilled holes follow EC5 §8.3.1.2 /
// Table 8.2 "as for non-predrilled nails". KKT has its own table. The steel 0.7 factor is applied later
// (applyEtaSpacingEccezioni).
export function isHbsFamily(family: string): boolean {
  return /^HBS/i.test(family || "");
}
export function isHbsTableApplicable(family: string, d: number): boolean {
  return isHbsFamily(family) && Number.isFinite(d) && d >= 6 && d <= 12;
}
export function etaSpacingNonHbsNonPredrilled(d: number, alphaDeg: number, rhok: number): Spacing {
  return ec5MinSpacing(d, alphaDeg, { predrilled: false, rhok, steelToTimber: false });
}
export function etaSpacingBaseNonPredrilled(family: string, d: number, alphaDeg: number, rhok: number): Spacing {
  return isHbsTableApplicable(family, d) ? etaSpacingHBS(d, alphaDeg) : etaSpacingNonHbsNonPredrilled(d, alphaDeg, rhok);
}
// Pure variant (family/substrate as parameters) used by the compatibility filters.
export function etaForoNonPredrilledFamilyPure(
  family: string,
  d: number,
  alphaDeg: number,
  substrateKey: string | null | undefined,
  rhok: number,
): Spacing {
  if (/KKT/i.test(family || "")) return etaSpacingKKT(d);
  if (substrateKey === "LVL Softwood") {
    if (isLvlAxialEdgeFamily(family) && (d === 7 || d === 9)) {
      return { ...etaSpacingBaseNonPredrilled(family, d, alphaDeg, rhok), ...etaSpacingLvlAxialEdge(d) };
    }
    if (isLvlWideFamily(family)) return etaSpacingLvlWide(d, alphaDeg);
  }
  return etaSpacingBaseNonPredrilled(family, d, alphaDeg, rhok);
}
// "Senza preforo": formula derived from the selected family (KKT, HBS d=6–12, LVL tables, else EC5 Table 8.2).
export function etaForoNonPredrilledFamily(ctx: EngineCtx, d: number, alpha: number, elementNum: number): Spacing {
  const v = ctx.v;
  const famiglia = v.etaFamiglia;
  if (/KKT/i.test(famiglia)) return etaSpacingKKT(d);
  const tipoKey = elementNum ? TIMBER_TIPO_MAP_REVERSE[elementNum === 1 ? v.mat1Tipo : v.mat2Tipo] : null;
  // ρk for the non-HBS families (EC5 Table 8.2 referenced by the ETA): element 2 when element 1 is a plate/panel,
  // otherwise the larger of the two.
  const isSteel = isSteelMat(v.materiale1) || isPanelMat(v.materiale1);
  const rhok2 = v.rhok2;
  const rhok1 = isSteel ? NaN : v.rhok1;
  const rhok = isSteel
    ? rhok2
    : Math.max(Number.isFinite(rhok1) ? rhok1 : 0, Number.isFinite(rhok2) ? rhok2 : 0) || rhok2;
  if (tipoKey === "LVL Softwood") {
    if (isLvlAxialEdgeFamily(famiglia) && (d === 7 || d === 9)) {
      return { ...etaSpacingBaseNonPredrilled(famiglia, d, alpha, rhok), ...etaSpacingLvlAxialEdge(d) };
    }
    if (isLvlWideFamily(famiglia)) return etaSpacingLvlWide(d, alpha);
  }
  return etaSpacingBaseNonPredrilled(famiglia, d, alpha, rhok);
}

// ETA-11/0030 exceptions by species/family and EC5 factors:
// - HBS PLATE / LBS HARDWOOD EVO, timber-timber: a1, a2 ×1.5;
// - steel-to-timber (EC5 §8.3.1.4(1)): element 2 spacings a1, a2 ×0.7;
// - panel-timber (EC5 §8.3.1.3(1)): a1, a2 ×0.85 on both elements;
// - Douglas fir, non-predrilled: a1 and a3 ×1.5;
// - optional a4,c = 3d for thin timber (t < 5d and a1/a3,t/a3,c ≥ 25d), otherwise not applied and reported;
// - axially loaded screws (EC5 §8.7.2(2)/Table 8.6, Fa > 0, t ≥ 12d): a1 ≥ 7d, a2 ≥ 5d.
export function applyEtaSpacingEccezioni(
  ctx: EngineCtx,
  sp: Spacing,
  d: number,
  elementNum: 1 | 2,
  nonPredrilled: boolean,
): Spacing {
  const v = ctx.v;
  const out: Spacing = { ...sp };
  const fam = v.etaFamiglia;
  const isPlateHw = (fam === "HBS PLATE" || fam === "LBS HARDWOOD EVO") && !isSteelMat(v.materiale1) && !isPanelMat(v.materiale1);
  ctx.ui.visible.etaPlateHwNote = isPlateHw;
  if (isPlateHw) {
    out.a1 *= 1.5;
    out.a2 *= 1.5;
  }
  if (elementNum === 2 && isSteelMat(v.materiale1)) {
    out.a1 *= 0.7;
    out.a2 *= 0.7;
  }
  if (isPanelMat(v.materiale1)) {
    out.a1 *= 0.85;
    out.a2 *= 0.85;
  }
  const douglas = elementNum === 1 ? v.douglas1 : v.douglas2;
  if (douglas && nonPredrilled) {
    out.a1 *= 1.5;
    out.a3t *= 1.5;
    out.a3c *= 1.5;
  }
  const warnKey = elementNum === 1 ? "a4reduce1Warn" : "a4reduce2Warn";
  const t = elementNum === 1 ? v.spessore1 : v.spessore2;
  if (elementNum === 1 ? v.a4reduce1 : v.a4reduce2) {
    const thin = Number.isFinite(t) && t < 5 * d;
    const okMinimi = out.a1 >= 25 * d && out.a3t >= 25 * d && out.a3c >= 25 * d;
    if (thin && okMinimi) {
      out.a4c = 3 * d;
      ctx.ui.texts[warnKey] = null;
    } else {
      const ta = ctx.t.engine.a4Reduce;
      ctx.ui.texts[warnKey] = !thin
        ? ta.notThin(Number.isFinite(t) ? t.toFixed(0) : "—", (5 * d).toFixed(0))
        : ta.spacing((25 * d).toFixed(0), `a1=${out.a1.toFixed(0)}, a3,t=${out.a3t.toFixed(0)}, a3,c=${out.a3c.toFixed(0)}`);
    }
  } else {
    ctx.ui.texts[warnKey] = null;
  }
  if (currentFa(ctx) > 0 && Number.isFinite(t) && t >= 12 * d) {
    out.a1 = Math.max(out.a1, 7 * d);
    out.a2 = Math.max(out.a2, 5 * d);
  }
  return out;
}

export function currentDiametro(ctx: EngineCtx): number {
  return ctx.v.etaDiametro;
}

export function computeSpacingAt(ctx: EngineCtx, alpha: number, elementNum: 1 | 2): Spacing {
  return ceilSpacing(computeSpacingAtRaw(ctx, alpha, elementNum));
}
export function computeSpacingAtRaw(ctx: EngineCtx, alpha: number, elementNum: 1 | 2): Spacing {
  const d = currentDiametro(ctx);
  const nonPredrilled = ctx.v.etaForo !== "predrilled";
  const sp =
    elementNum === 1 && isPanelMat(ctx.v.materiale1)
      ? panelSpacing1(ctx, d, alpha, !nonPredrilled)
      : nonPredrilled
        ? etaForoNonPredrilledFamily(ctx, d, alpha, elementNum)
        : etaSpacingPredrilled(d, alpha);
  return elementNum ? applyEtaSpacingEccezioni(ctx, sp, d, elementNum, nonPredrilled) : sp;
}
